import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getCountryFlag, getCountryName } from '../utils/countries';
import { POWER_CATEGORIES } from '../constants/powerGroups';
import CountryDisplay from './CountryDisplay';
import CountrySelect from './CountrySelect';
import MedalIcon from './MedalIcon';
import Pagination from './Pagination';
import './RatingsPage.css';

// Годовой рейтинг гонщиков: очки, гонки и километры на этапах всех турниров
// сезона, см. RatingsService.
//
// Фильтры и страница живут в адресе: срезом рейтинга можно поделиться ссылкой,
// а возврат из карточки гонщика приводит ровно туда, откуда ее открыли.
// На десктопе список листается пагинацией, на мобильном — лентой, которая
// подгружает следующую страницу при прокрутке (как список пользователей).

const FILTERS = ['gender', 'country', 'age_group', 'power_category'];
const PAGE_PARAM = 'page';

const EMPTY_OPTIONS = {
  genders: [],
  countries: [],
  country_counts: {},
  age_groups: [],
  power_categories: []
};

const isMobileWidth = () => typeof window !== 'undefined' && window.innerWidth <= 768;

// Номера страниц с from по to включительно
const pageRange = (from, to) => Array.from({ length: Math.max(to - from + 1, 0) }, (_, index) => from + index);

const fetchRatingsPage = (filtersQuery, page) => {
  const params = new URLSearchParams(filtersQuery);
  if (page > 1) params.set(PAGE_PARAM, String(page));
  const query = params.toString();

  return axios.get(`/api/ratings${query ? `?${query}` : ''}`).then((response) => response.data);
};

// Пока лента подгружается, рейтинг мог пересчитаться и гонщик — сдвинуться на
// соседнюю страницу. Второй раз одну строку не показываем
const uniqueRiders = (riders) => {
  const seen = new Set();
  return riders.filter((rider) => !seen.has(rider.user_id) && seen.add(rider.user_id));
};

const riderName = (rider, language) =>
  language !== 'ru' && rider.first_name_eng && rider.last_name_eng
    ? `${rider.first_name_eng} ${rider.last_name_eng}`
    : `${rider.first_name} ${rider.last_name}`;

// Значок пола — те же глифы, что в списке пользователей (UsersList)
const GenderIcon = ({ gender, label }) => {
  if (gender !== 'male' && gender !== 'female') return null;

  return (
    <span className={`ratings-gender ratings-gender--${gender}`} role="img" aria-label={label} title={label}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        {gender === 'male' ? (
          <>
            <circle cx="10" cy="14" r="5" />
            <path d="M19 5l-7 7M19 5h-5M19 5v5" />
          </>
        ) : (
          <>
            <circle cx="12" cy="8" r="5" />
            <path d="M12 13v8M9 18h6" />
          </>
        )}
      </svg>
    </span>
  );
};

const RatingsPage = () => {
  const { user } = useAuth();
  const { t, language, pluralize } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const pageRef = useRef(null);
  const sentinelRef = useRef(null);
  const requestIdRef = useRef(0);

  const query = searchParams.toString();

  // Срез без номера страницы: листание не должно перезапускать загрузку среза
  const filtersQuery = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(PAGE_PARAM);
    return params.toString();
  }, [searchParams]);

  // На десктопе страница в адресе — открытая страница таблицы, на мобильном —
  // сколько страниц ленты уже подгружено
  const urlPage = Math.max(Math.floor(Number(searchParams.get(PAGE_PARAM))) || 1, 1);
  const urlPageRef = useRef(urlPage);
  urlPageRef.current = urlPage;

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileWidth());

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Загружает страницы среза с from по to. append — дописать строки к уже
  // показанным (лента), иначе заменить. Ответ устаревшего запроса — пользователь
  // успел сменить фильтр или страницу — отбрасывается
  const load = useCallback(async (from, to, append = false) => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(false);
    }
    setLoadMoreFailed(false);

    try {
      const first = await fetchRatingsPage(filtersQuery, from);
      // Страниц в срезе может оказаться меньше, чем записано в адресе: лишние
      // запросы вернули бы последнюю страницу еще раз
      const rest = await Promise.all(
        pageRange(from + 1, Math.min(to, first.pagination.total_pages))
          .map((page) => fetchRatingsPage(filtersQuery, page))
      );
      if (!isCurrent()) return;

      const pages = [first, ...rest];
      const rows = pages.flatMap((page) => page.riders);

      setData((previous) => ({
        ...pages[pages.length - 1],
        riders: uniqueRiders(append && previous ? [...previous.riders, ...rows] : rows)
      }));
    } catch {
      if (!isCurrent()) return;

      if (append) {
        setLoadMoreFailed(true);
      } else {
        setError(true);
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filtersQuery]);

  // Загрузка среза: при открытии страницы, смене фильтров и переходе между
  // десктопом и мобильным. На мобильном возврат из карточки гонщика добирает
  // все подгруженные раньше страницы ленты, иначе начало списка пропадет
  useEffect(() => {
    const page = urlPageRef.current;

    if (isMobile) {
      load(1, page);
    } else {
      load(page, page);
    }
  }, [load, isMobile]);

  const setUrlPage = useCallback((page) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (page > 1) {
        next.set(PAGE_PARAM, String(page));
      } else {
        next.delete(PAGE_PARAM);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const goToPage = (page) => {
    setUrlPage(page);
    load(page, page);
    pageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hasMore = Boolean(data) && data.pagination.current_page < data.pagination.total_pages;

  const loadMore = useCallback(() => {
    const next = data.pagination.current_page + 1;

    setUrlPage(next);
    load(next, next, true);
  }, [data, load, setUrlPage]);

  // Лента: следующая страница подгружается, когда низ списка показался на экране.
  // Пока идет загрузка или последняя попытка сорвалась, не следим — повтор по кнопке
  useEffect(() => {
    if (!isMobile || !hasMore || loading || loadingMore || loadMoreFailed) return undefined;

    const target = sentinelRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    });
    observer.observe(target);

    return () => observer.disconnect();
  }, [isMobile, hasMore, loading, loadingMore, loadMoreFailed, loadMore]);

  // Смена фильтра сбрасывает страницу: номер страницы другой выборки ничего не значит
  const setFilter = (name, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(name, value);
    } else {
      next.delete(name);
    }
    next.delete(PAGE_PARAM);
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => setSearchParams({}, { replace: true });

  const filterValue = (name) => searchParams.get(name) || '';
  const hasFilters = FILTERS.some((name) => filterValue(name));

  const options = data?.filter_options || EMPTY_OPTIONS;
  const year = data?.year ?? new Date().getFullYear();
  const riders = data?.riders || [];
  const total = data?.pagination?.total_count ?? 0;

  const withYear = (key) => t(key).replace('{year}', year);

  // Подпись к числу. Формы слова лежат в локали: [1, 2–4, 5 и больше]
  const unit = (count, key) => {
    const [one, few, many] = t(key);
    return pluralize(count, one, few, many);
  };

  const ridersCount = `${total} ${unit(total, 'ratings.riders_forms')}`;

  const powerLabel = (category) => {
    const found = POWER_CATEGORIES.find((item) => item.cat === category);
    return found ? `${category} (${found.wkg} ${t('users.powerUnit')})` : category;
  };

  // Карточка гонщика вернет на этот же срез рейтинга
  const riderLinkState = {
    backTo: {
      pathname: `/ratings${query ? `?${query}` : ''}`,
      labelKey: 'ratings.back_to_ratings'
    }
  };

  const renderBoard = () => {
    if (error) {
      return <div className="ratings-message">{t('ratings.load_error')}</div>;
    }

    if (!data) {
      return <div className="ratings-message">{t('common.loading')}</div>;
    }

    if (riders.length === 0) {
      return (
        <div className="ratings-message">
          {hasFilters ? t('ratings.empty_filtered') : withYear('ratings.empty')}
        </div>
      );
    }

    return (
      <table className={`ratings-table${loading ? ' ratings-table--loading' : ''}`}>
        <thead>
          <tr>
            <th className="ratings-position">{t('ratings.position')}</th>
            <th>{t('ratings.rider')}</th>
            <th className="ratings-country">{t('ratings.country')}</th>
            <th className="ratings-team">{t('ratings.team')}</th>
            <th className="ratings-power">{t('ratings.power')}</th>
            <th className="ratings-age">{t('ratings.age')}</th>
            <th className="ratings-number">{t('ratings.races')}</th>
            <th className="ratings-number">{t('ratings.distance')}</th>
            <th className="ratings-number">{t('ratings.points')}</th>
          </tr>
        </thead>
        <tbody>
          {riders.map((rider) => (
            <tr
              key={rider.user_id}
              className={rider.user_id === user?.id ? 'ratings-row--me' : undefined}
            >
              <td className="ratings-position">
                {rider.position <= 3 ? <MedalIcon place={rider.position} /> : rider.position}
              </td>
              {/* flex — на обертке, а не на ячейке: ячейка с display: flex
                  выпадает из таблицы, и фон строки рвется */}
              <td className="ratings-rider">
                <div className="ratings-rider-inner">
                  <div className="ratings-avatar-wrap">
                    <img
                      src={getUserAvatarUrl(rider.avatar)}
                      alt=""
                      className="ratings-avatar"
                      onError={handleUserAvatarError}
                    />
                    <GenderIcon gender={rider.gender} label={rider.gender && t(`ratings.${rider.gender}`)} />
                  </div>
                  <div className="ratings-identity">
                    <Link
                      to={`/riders/${rider.user_id}`}
                      state={riderLinkState}
                      className="ratings-name"
                      title={t('results.open_rider')}
                    >
                      {riderName(rider, language)}
                    </Link>
                    <div className="ratings-nickname">@{rider.nickname}</div>
                    {/* На узком экране колонки складываются в две строки под
                        именем: флаг, мощность и возраст, ниже очки, гонки и км */}
                    <div className="ratings-meta">
                      {rider.country && <span>{getCountryFlag(rider.country)}</span>}
                      {rider.power_category && <span className="ratings-badge">{rider.power_category}</span>}
                      {rider.age_group && <span className="ratings-pill">{rider.age_group}</span>}
                    </div>
                    <div className="ratings-stats">
                      <span className="ratings-stat">
                        <strong className="ratings-stats-points">{rider.points}</strong>
                        <span className="ratings-stat-unit">{unit(rider.points, 'ratings.points_forms')}</span>
                      </span>
                      <span className="ratings-stat">
                        <strong>{rider.races}</strong>
                        <span className="ratings-stat-unit">{unit(rider.races, 'ratings.races_forms')}</span>
                      </span>
                      <span className="ratings-stat">
                        <strong>{rider.distance_km}</strong>
                        <span className="ratings-stat-unit">{t('ratings.km')}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </td>
              <td
                className="ratings-country"
                title={rider.country ? getCountryName(rider.country, language) : undefined}
              >
                <CountryDisplay country={rider.country} language={language} />
              </td>
              <td className="ratings-team">
                {rider.team ? <span title={rider.team.name}>{rider.team.name}</span> : '—'}
              </td>
              <td className="ratings-power">
                {rider.power_category ? (
                  <span
                    className="ratings-badge"
                    title={rider.power_group ? `${rider.power_group} ${t('users.powerUnit')}` : undefined}
                  >
                    {rider.power_category}
                  </span>
                ) : '—'}
              </td>
              <td className="ratings-age">
                {rider.age_group ? <span className="ratings-pill">{rider.age_group}</span> : '—'}
              </td>
              <td className="ratings-number">{rider.races}</td>
              <td className="ratings-number">{rider.distance_km}</td>
              <td className="ratings-number ratings-points">{rider.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Низ списка: на десктопе пагинация под таблицей, как в списке пользователей,
  // на мобильном — подгрузка ленты
  const renderFooter = () => {
    if (!data || error || riders.length === 0) return null;

    if (!isMobile) {
      return data.pagination.total_pages > 1 ? (
        <div className="ratings-board-footer">
          <Pagination pagination={data.pagination} onPageChange={goToPage} t={t} compact />
        </div>
      ) : null;
    }

    return (
      <>
        {loadingMore && <div className="ratings-loading-more">{t('common.loading')}</div>}
        {loadMoreFailed && (
          <button type="button" className="ratings-load-more-retry" onClick={loadMore}>
            {t('ratings.load_more_error')}
          </button>
        )}
        {hasMore && <div ref={sentinelRef} className="ratings-sentinel" aria-hidden="true" />}
      </>
    );
  };

  return (
    <div className="ratings-page" ref={pageRef}>
      <header className="ratings-header">
        <div>
          <h1>{withYear('ratings.heading')}</h1>
          <p className="ratings-subtitle">{withYear('ratings.subtitle')}</p>
        </div>
        {data && !error && <div className="ratings-count">{ridersCount}</div>}
      </header>

      <section className="ratings-filters">
        <label className="ratings-filter">
          <span>{t('ratings.filter_gender')}</span>
          <select value={filterValue('gender')} onChange={(e) => setFilter('gender', e.target.value)}>
            <option value="">{t('ratings.all')}</option>
            {options.genders.map((gender) => (
              <option key={gender} value={gender}>{t(`ratings.gender_${gender}`)}</option>
            ))}
          </select>
        </label>

        <label className="ratings-filter">
          <span>{t('ratings.filter_country')}</span>
          <CountrySelect
            value={filterValue('country')}
            onChange={(value) => setFilter('country', value)}
            language={language}
            placeholder={t('ratings.all')}
            countryCounts={options.country_counts}
            onlyCountries={options.countries}
          />
        </label>

        <label className="ratings-filter">
          <span>{t('ratings.filter_age')}</span>
          <select value={filterValue('age_group')} onChange={(e) => setFilter('age_group', e.target.value)}>
            <option value="">{t('ratings.all')}</option>
            {options.age_groups.map((group) => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
        </label>

        <label className="ratings-filter">
          <span>{t('ratings.filter_power')}</span>
          <select value={filterValue('power_category')} onChange={(e) => setFilter('power_category', e.target.value)}>
            <option value="">{t('ratings.all')}</option>
            {options.power_categories.map((category) => (
              <option key={category} value={category}>{powerLabel(category)}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="ratings-reset"
          onClick={resetFilters}
          disabled={!hasFilters}
        >
          {t('ratings.reset')}
        </button>
      </section>

      <section className="ratings-board">
        {renderBoard()}
        {renderFooter()}
      </section>
    </div>
  );
};

export default RatingsPage;
